/**
 * Extrait les chemins de propriétés d'un objet et retourne un array de strings.
 * Pour chaque propriété:
 * - Si la valeur est une string non-vide, le chemin est concaténé avec la valeur
 * - Si la valeur est une string vide, seul le chemin est retourné
 * - Si la valeur est un objet, la fonction parcourt récursivement
 *
 * @param obj L'objet à parcourir
 * @param currentPath Le chemin actuel (utilisé pour la récursion)
 * @returns Array de strings représentant les chemins de propriétés
 *
 * @example
 * ```typescript
 * const obj = { a: { b: "*", c: null }, d: "*.*.*" };
 * const paths = getObjectPaths(obj);
 * // Résultat: ["a.b.*", "a.c", "d.*.*.*"]
 * ```
 */

export type FieldObject = { [key: string]: string | FieldObject | null | (string | FieldObject)[] };

export function toFieldStringArray(obj: FieldObject, currentPath = ""): string[] {
    const paths: string[] = [];

    for (const [key, value] of Object.entries(obj)) {
        const newPath = currentPath ? `${currentPath}.${key}` : key;

        if (!value) {
            paths.push(newPath);
        } else if (typeof value === "string") {
            paths.push(`${newPath}.${value}`);
        } else if (typeof value === "object" && !Array.isArray(value)) {
            paths.push(...toFieldStringArray(value, newPath));
        } else if (Array.isArray(value)) {
            paths.push(
                ...value.flatMap((item) => {
                    if (typeof item === "string") {
                        return [`${newPath}.${item}`];
                    } else if (typeof item === "object" && !Array.isArray(item)) {
                        return toFieldStringArray(item, newPath);
                    }
                    return [];
                })
            );
        }
    }

    return paths;
}
